import { useState } from 'preact/hooks';
import type { JSX } from 'preact';
import type { Recipe, Components, RecipeCategory, GrocerySection, MealType, Status, Cuisine } from '../types';
import { newId } from '../lib/ids';
import { sourceNameForUrl } from '../lib/sources';

const CUISINES: Cuisine[] = ['vietnamese','chinese','korean','japanese','thai','american','other'];
const CATEGORIES: RecipeCategory[] = [
  'sandwich','pinwheel','quesadilla','bento','thermos',
  'pasta','rice-grain','mini-muffin','breakfast-for-lunch','dippable','hidden-veggie',
];
const SECTIONS: GrocerySection[] = ['produce','dairy','bakery','meat','pantry','frozen','other'];
const COMPONENT_KEYS: (keyof Components)[] = ['protein','grain','veggie','fruit','fat'];
const MEAL_TYPES: MealType[] = ['lunch','dinner','breakfast','snack'];

function blank(): Recipe {
  return {
    id: newId(), name: '', category: 'sandwich', cuisine: null, description: '',
    meal_types: ['lunch'],
    components: { protein: false, grain: false, veggie: false, fruit: false, fat: false },
    hidden_veggie: false, is_make_ahead: false, prep_minutes: 5,
    cook_minutes: null, servings: null,
    safety_notes: [], instructions: [], ingredients: [],
    image_url: null, image_focus: null, source_url: null, source_name: null, is_seed: false, archived: false,
    status: null, notes: '',
    created_at: new Date().toISOString(), updated_at: new Date().toISOString(),
  };
}

export function RecipeForm(props: {
  initial?: Recipe;
  onSave: (r: Recipe) => void;
  onCancel: () => void;
  onDelete?: () => void;
}) {
  const [r, setR] = useState<Recipe>(props.initial ?? blank());
  function patch<K extends keyof Recipe>(key: K, val: Recipe[K]) {
    setR(prev => ({ ...prev, [key]: val, updated_at: new Date().toISOString() }));
  }
  function patchSourceUrl(rawValue: string) {
    const url = rawValue || null;
    setR(prev => ({
      ...prev,
      source_url: url,
      source_name: sourceNameForUrl(url),
      updated_at: new Date().toISOString(),
    }));
  }
  function patchComponent(key: keyof Components, val: boolean) {
    setR(prev => ({ ...prev, components: { ...prev.components, [key]: val }, updated_at: new Date().toISOString() }));
  }
  function setIngredient(idx: number, ing: Recipe['ingredients'][number]) {
    setR(prev => ({ ...prev, ingredients: prev.ingredients.map((x, i) => i === idx ? ing : x) }));
  }
  function addIngredient() {
    setR(prev => ({ ...prev, ingredients: [...prev.ingredients, { name: '', quantity: '', grocery_section: 'pantry' }] }));
  }
  function removeIngredient(idx: number) {
    setR(prev => ({ ...prev, ingredients: prev.ingredients.filter((_, i) => i !== idx) }));
  }
  function setSafety(idx: number, val: string) {
    setR(prev => ({ ...prev, safety_notes: prev.safety_notes.map((x, i) => i === idx ? val : x) }));
  }
  function addSafety() { setR(prev => ({ ...prev, safety_notes: [...prev.safety_notes, ''] })); }
  function removeSafety(idx: number) {
    setR(prev => ({ ...prev, safety_notes: prev.safety_notes.filter((_, i) => i !== idx) }));
  }
  function setStep(idx: number, val: string) {
    setR(prev => ({ ...prev, instructions: prev.instructions.map((x, i) => i === idx ? val : x) }));
  }
  function addStep() { setR(prev => ({ ...prev, instructions: [...prev.instructions, ''] })); }
  function removeStep(idx: number) {
    setR(prev => ({ ...prev, instructions: prev.instructions.filter((_, i) => i !== idx) }));
  }

  return (
    <form onSubmit={e => {
      e.preventDefault();
      const cleaned = (r.source_url ?? '').trim();
      const finalRecipe = { ...r, source_url: cleaned === '' ? null : cleaned };
      props.onSave(finalRecipe);
    }} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <Field label="Name">
        <input value={r.name} onInput={e => patch('name', (e.target as HTMLInputElement).value)} required style={inputStyle} />
      </Field>
      <Field label="Category">
        <select value={r.category ?? ''} onChange={e => patch('category', ((e.target as HTMLSelectElement).value || null) as RecipeCategory | null)} style={inputStyle}>
          <option value="">— none —</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </Field>
      <Field label="Description">
        <textarea value={r.description} onInput={e => patch('description', (e.target as HTMLTextAreaElement).value)} style={{ ...inputStyle, minHeight: 60 }} />
      </Field>
      <Field label="Meal types">
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {MEAL_TYPES.map(mt => (
            <label key={mt} style={{ display: 'inline-flex', gap: 6 }}>
              <input type="checkbox" checked={r.meal_types.includes(mt)}
                onChange={e => {
                  const checked = (e.target as HTMLInputElement).checked;
                  patch('meal_types', checked
                    ? [...r.meal_types, mt]
                    : r.meal_types.filter(x => x !== mt));
                }} />
              {mt}
            </label>
          ))}
        </div>
      </Field>
      <Field label="Lunch Formula components">
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {COMPONENT_KEYS.map(c => (
            <label key={c} style={{ display: 'inline-flex', gap: 6 }}>
              <input type="checkbox" checked={r.components[c]} onChange={e => patchComponent(c, (e.target as HTMLInputElement).checked)} />
              {c}
            </label>
          ))}
        </div>
      </Field>
      <Field label="Flags">
        <label><input type="checkbox" checked={r.hidden_veggie} onChange={e => patch('hidden_veggie', (e.target as HTMLInputElement).checked)} /> Hidden veggie</label>{' '}
        <label><input type="checkbox" checked={r.is_make_ahead} onChange={e => patch('is_make_ahead', (e.target as HTMLInputElement).checked)} /> Make-ahead</label>
      </Field>
      <Field label="Prep minutes">
        <input type="number" value={r.prep_minutes} onInput={e => patch('prep_minutes', Number((e.target as HTMLInputElement).value))} style={{ ...inputStyle, width: 100 }} />
      </Field>
      <Field label="Cook minutes (optional)">
        <input type="number" value={r.cook_minutes ?? ''} onInput={e => patch('cook_minutes', (e.target as HTMLInputElement).value === '' ? null : Number((e.target as HTMLInputElement).value))} style={{ ...inputStyle, width: 100 }} />
      </Field>
      <Field label="Servings (optional)">
        <input type="number" value={r.servings ?? ''} onInput={e => patch('servings', (e.target as HTMLInputElement).value === '' ? null : Number((e.target as HTMLInputElement).value))} style={{ ...inputStyle, width: 100 }} />
      </Field>
      <Field label="Cuisine (optional)">
        <select value={r.cuisine ?? ''} onChange={e => patch('cuisine', ((e.target as HTMLSelectElement).value || null) as Cuisine | null)} style={inputStyle}>
          <option value="">— none —</option>
          {CUISINES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </Field>
      <Field label="Status (Try?)">
        <select value={r.status ?? ''} onChange={e => patch('status', ((e.target as HTMLSelectElement).value || null) as Status | null)} style={inputStyle}>
          <option value="">—</option><option value="yes">Yes</option><option value="maybe">Maybe</option><option value="no">No</option>
        </select>
      </Field>
      <Field label="Image URL (optional)">
        <input value={r.image_url ?? ''} onInput={e => patch('image_url', (e.target as HTMLInputElement).value || null)} style={inputStyle} placeholder="/recipes/turkey-cheddar.jpg or https://..." />
      </Field>
      <Field label="Source URL (optional)">
        <input value={r.source_url ?? ''} onInput={e => patchSourceUrl((e.target as HTMLInputElement).value)} style={inputStyle} />
      </Field>

      <fieldset style={fieldset}>
        <legend className="label-caps">Ingredients</legend>
        {r.ingredients.map((ing, i) => (
          <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
            <input value={ing.name} onInput={e => setIngredient(i, { ...ing, name: (e.target as HTMLInputElement).value })} placeholder="name" style={{ ...inputStyle, flex: 2 }} />
            <input value={ing.quantity} onInput={e => setIngredient(i, { ...ing, quantity: (e.target as HTMLInputElement).value })} placeholder="qty" style={{ ...inputStyle, flex: 1 }} />
            <select value={ing.grocery_section} onChange={e => setIngredient(i, { ...ing, grocery_section: (e.target as HTMLSelectElement).value as GrocerySection })} style={{ ...inputStyle, flex: 1 }}>
              {SECTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <button type="button" onClick={() => removeIngredient(i)} style={{ ...smallBtn }}>×</button>
          </div>
        ))}
        <button type="button" onClick={addIngredient} style={smallBtn}>+ ingredient</button>
      </fieldset>

      <fieldset style={fieldset}>
        <legend className="label-caps">Instructions</legend>
        {r.instructions.map((s, i) => (
          <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 4, alignItems: 'flex-start' }}>
            <span style={{ minWidth: 18, paddingTop: 8, fontSize: 12, color: 'var(--plum-soft)', fontWeight: 600 }}>{i + 1}.</span>
            <textarea value={s} onInput={e => setStep(i, (e.target as HTMLTextAreaElement).value)} style={{ ...inputStyle, flex: 1, minHeight: 40 }} />
            <button type="button" onClick={() => removeStep(i)} style={smallBtn}>×</button>
          </div>
        ))}
        <button type="button" onClick={addStep} style={smallBtn}>+ step</button>
      </fieldset>

      <fieldset style={fieldset}>
        <legend className="label-caps">Safety notes</legend>
        {r.safety_notes.map((s, i) => (
          <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
            <input value={s} onInput={e => setSafety(i, (e.target as HTMLInputElement).value)} style={{ ...inputStyle, flex: 1 }} />
            <button type="button" onClick={() => removeSafety(i)} style={smallBtn}>×</button>
          </div>
        ))}
        <button type="button" onClick={addSafety} style={smallBtn}>+ safety note</button>
      </fieldset>

      <Field label="Notes">
        <textarea value={r.notes} onInput={e => patch('notes', (e.target as HTMLTextAreaElement).value)} style={{ ...inputStyle, minHeight: 80 }} />
      </Field>

      <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
        <button type="submit" style={primaryBtn}>Save</button>
        <button type="button" onClick={props.onCancel} style={secondaryBtn}>Cancel</button>
        {props.onDelete && (
          <button type="button" onClick={() => props.onDelete!()}
            style={{ ...secondaryBtn, marginLeft: 'auto' }}>Archive</button>
        )}
      </div>
    </form>
  );
}

function Field(props: { label: string; children: any }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span className="label-caps">{props.label}</span>
      {props.children}
    </label>
  );
}

const inputStyle: JSX.CSSProperties = { padding: '12px 14px', height: 44, borderRadius: 'var(--radius-md)', border: '1px solid var(--hairline)', background: 'white', fontSize: 14, color: 'var(--ink)', fontFamily: 'inherit', lineHeight: 1.4 };
const fieldset: JSX.CSSProperties = { border: '1px solid var(--hairline)', borderRadius: 'var(--radius-sm)', padding: 8 };
const primaryBtn: JSX.CSSProperties = { padding: '12px 20px', height: 44, borderRadius: 'var(--radius-md)', background: 'var(--pink-primary)', color: 'var(--on-primary)', border: 'none', fontSize: 14, fontWeight: 600, cursor: 'pointer', lineHeight: 1, fontFamily: 'inherit' };
const secondaryBtn: JSX.CSSProperties = { padding: '12px 20px', height: 44, borderRadius: 'var(--radius-md)', background: 'white', color: 'var(--ink)', border: '1px solid var(--hairline)', fontSize: 14, fontWeight: 600, cursor: 'pointer', lineHeight: 1, fontFamily: 'inherit' };
const smallBtn: JSX.CSSProperties = { padding: '7px 12px', height: 32, borderRadius: 'var(--radius-sm)', background: 'white', color: 'var(--ink)', border: '1px solid var(--hairline)', fontSize: 12, fontWeight: 600, cursor: 'pointer', lineHeight: 1 };
